import React, { useState } from 'react';
import { Clock, Users, Euro, CheckCircle } from 'lucide-react';
import InfoModal from './InfoModal';
import AtelierModal from './AtelierModal';

const AutresFormations = () => {
  const [selectedInfo, setSelectedInfo] = useState<{type: 'formation' | 'atelier', title: string, price: string, duration: string, description: string} | null>(null);
  const [showAtelierModal, setShowAtelierModal] = useState(false);
  
  const formations = [
    {
      title: "Formation Pizzaïolo",
      description: "Formation de base pour devenir pizzaïolo professionnel",
      duration: "35 heures",
      participants: "3 élèves max",
      price: "1000€",
      features: [
        "Techniques de base de la pizza",
        "Pâte traditionnelle",
        "Garniture classique",
        "Cuisson au four",
        "Hygiène et sécurité"
      ],
      image: "./images/chef_formation_2.jpeg"
    },
    {
      title: "Hygiène Alimentaire HACCP",
      description: "Formation obligatoire pour la restauration commerciale",
      duration: "14 heures",
      participants: "Groupe",
      price: "350€",
      features: [
        "Réglementation sanitaire",
        "Analyse des dangers",
        "Points critiques de contrôle",
        "Traçabilité alimentaire",
        "Certification obligatoire"
      ],
      image: "./images/chef_formation_3.jpeg"
    },
    {
      title: "Création d'Entreprise",
      description: "Accompagnement pour créer votre pizzeria",
      duration: "21 heures",
      participants: "Individuel",
      price: "650€",
      features: [
        "Business plan",
        "Étude de marché",
        "Financement",
        "Réglementation",
        "Marketing"
      ],
      image: "./images/chef_formation_4.webp"
    }
  ];

  return (
    <section id="formations" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Nos Autres Formations
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Complétez votre parcours avec nos formations complémentaires pour devenir un professionnel accompli.
          </p>
        </div>

        {/* Formations Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {formations.map((formation, index) => (
            <div key={index} className="bg-gray-50 rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              <div className="relative h-48">
                <img 
                  src={formation.image} 
                  alt={formation.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-4 left-4 text-white">
                  <h3 className="text-xl font-bold">{formation.title}</h3>
                </div>
              </div>
              
              <div className="p-6">
                <p className="text-gray-600 mb-6">{formation.description}</p>
                
                {/* Info Cards */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                  <div className="bg-white p-3 rounded-lg text-center">
                    <Clock className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="text-xs font-semibold">{formation.duration}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg text-center">
                    <Users className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="text-xs font-semibold">{formation.participants}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg text-center">
                    <Euro className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="text-xs font-semibold">{formation.price}</div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2 mb-6">
                  {formation.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <button 
                  onClick={() => setSelectedInfo({
                    type: 'formation',
                    title: formation.title,
                    price: formation.price,
                    duration: formation.duration,
                    description: `Formation complète de ${formation.duration} pour maîtriser ${formation.title.toLowerCase()}. ${formation.description}`
                  })}
                  className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  En savoir plus
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Ateliers pour Particuliers */}
        <div className="mt-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl p-8 text-center">
          <h3 className="text-3xl font-bold text-red-900 mb-4">Ateliers pour Particuliers</h3>
          <p className="text-red-800 mb-6 max-w-2xl mx-auto">
            Découvrez l'art de la pizza napolitaine lors de nos ateliers conviviaux. 
            Parfait pour les passionnés et les curieux !
          </p>
          <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <h4 className="font-bold text-red-900 mb-2">Atelier Pizza</h4>
              <p className="text-red-800 text-sm mb-2">3 heures - 90€</p>
              <p className="text-red-800 text-sm">Apprenez les bases de la pizza napolitaine</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
              <h4 className="font-bold text-red-900 mb-2">Atelier Pizza & Socca</h4>
              <p className="text-red-800 text-sm mb-2">4 heures - 110€</p>
              <p className="text-red-800 text-sm">Pizza + spécialité niçoise Socca</p>
            </div>
          </div>
          <button 
            onClick={() => setShowAtelierModal(true)}
            className="mt-6 bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            Réserver un Atelier
          </button>
        </div>
      </div>
      
      {/* Info Modal */}
      <InfoModal 
        isOpen={selectedInfo !== null}
        onClose={() => setSelectedInfo(null)}
        type={selectedInfo?.type || 'formation'}
        title={selectedInfo?.title || ''}
        price={selectedInfo?.price || ''}
        duration={selectedInfo?.duration || ''}
        description={selectedInfo?.description || ''}
      />
      
      {/* Atelier Modal */}
      <AtelierModal 
        isOpen={showAtelierModal}
        onClose={() => setShowAtelierModal(false)}
      />
    </section>
  );
};

export default AutresFormations;